import React, {useEffect} from 'react';
import {view} from '@sail/ui';
import {useSelector, setValue} from 'src/v2/state/store';
import {WalletSelectScreen} from 'src/v2/components/walletSelect/WalletSelectScreen';
import {WalletConnectScreen} from 'src/v2/components/walletConnect/WalletConnectScreen';
import {NetworkSwitchScreen} from 'src/v2/components/networkSwitch/NetworkSwitchScreen';
import {WalletSwitchScreen} from 'src/v2/components/walletSwitch/WalletSwitchScreen';
import {PayScreen} from 'src/v2/components/pay/PayScreen';
import {SetupScreen} from 'src/v2/components/setup/SetupScreen';
import {SuccessScreen} from 'src/v2/components/success/SuccessScreen';
import {ProcessingScreen} from 'src/v2/components/processing/ProcessingScreen';
import {CanceledScreen} from 'src/v2/components/canceled/CanceledScreen';
import {ErroredScreen} from 'src/v2/components/error/ErroredScreen';
import {InstallWalletScreen} from 'src/v2/components/installWallet/InstallWalletScreen';
import {ExchangeScreen} from 'src/v2/components/exchangeScreen/ExchangeScreen';
import {DirectDepositScreen} from 'src/v2/components/directDeposit/DirectDepositScreen';
import {PayTitle} from 'src/v2/components/screen/PayTitle';
import {useSessionContext} from 'src/state/usePayinSessionContext';
import {SetupTitle} from 'src/v2/components/screen/SetupTitle';
import {NetworkSwitchingOverlay} from 'src/v2/components/overlays/NetworkSwitchingOverlay';
import {logger} from 'src/utils/logger';
import {validateWalletForCheckout} from 'src/v2/util/validateWalletForCheckout';
import {useMiradorIds} from 'src/v2/state/fingerprinting/useMiradorIds';

const PrepaymentWrapper = ({children}: {children: React.ReactNode}) => {
  const {mode} = useSessionContext();
  const title = mode === 'pay' ? <PayTitle /> : <SetupTitle />;

  return (
    <view.div
      css={{
        stack: 'y',
        width: '388px',
        gapY: 'medium',
        boxShadow:
          '0px -4px 32px rgba(0, 0, 0, 0.04), 0px 12px 32px rgba(0, 0, 0, 0.04)',
        borderRadius: '20px',
        borderWidth: 'small',
        padding: 'large',
        backgroundColor: '#FCFCFC',
        flex: 'none',
      }}
    >
      {title}

      {children}
    </view.div>
  );
};

const ScreenComponent = () => {
  const screen = useSelector((state) => state.screen);
  const activeWallet = useSelector(
    (state) => state.walletConnection.activeWallet,
  );
  const connectedWallets = useSelector(
    (state) => state.walletConnection.connectedWallets,
  );
  const supportedCurrencies = useSelector((state) => state.supportedCurrencies);

  // Clear the error banner when the screen changes
  useEffect(() => {
    setValue((state) => {
      state.errorBanner = null;
    });

    logger.log('crypto_payin_session.screen.rendered', {
      screen,
    });
  }, [screen]);

  switch (screen) {
    case 'WALLET_SELECT_GRID':
      return (
        <PrepaymentWrapper>
          <WalletSelectScreen initialShowGrid />
        </PrepaymentWrapper>
      );
    case 'WALLET_SELECT':
      return (
        <PrepaymentWrapper>
          <WalletSelectScreen initialShowGrid={false} />
        </PrepaymentWrapper>
      );
    case 'WALLET_SWITCH':
      return (
        <PrepaymentWrapper>
          <WalletSwitchScreen />
        </PrepaymentWrapper>
      );
    case 'NETWORK_SELECT':
      return (
        <PrepaymentWrapper>
          <WalletConnectScreen />
        </PrepaymentWrapper>
      );
    case 'NETWORK_SWITCH':
      return (
        <PrepaymentWrapper>
          <NetworkSwitchScreen />
        </PrepaymentWrapper>
      );
    case 'EXCHANGE':
      return (
        <PrepaymentWrapper>
          <ExchangeScreen />
        </PrepaymentWrapper>
      );
    case 'DIRECT_DEPOSIT':
      return (
        <PrepaymentWrapper>
          <DirectDepositScreen />
        </PrepaymentWrapper>
      );
    case 'PAY':
    case 'SETUP': {
      const result = validateWalletForCheckout({
        activeWallet,
        connectedWallets,
        supportedCurrencies,
      });

      if ('redirect' in result) {
        setValue((state) => {
          state.screen = result.redirect;
        });
        return null;
      }

      const CheckoutScreen = screen === 'PAY' ? PayScreen : SetupScreen;

      return (
        <PrepaymentWrapper>
          <CheckoutScreen
            // Make sure we renders the screen when the active wallet changes. This resets all local states.
            key={`${result.activeWallet.id}-${result.activeWallet.chainId}`}
            activeWallet={result.activeWallet}
          />
        </PrepaymentWrapper>
      );
    }
    case 'INSTALL_WALLET':
      return (
        <PrepaymentWrapper>
          <InstallWalletScreen />
        </PrepaymentWrapper>
      );
    case 'SUCCESS':
      return <SuccessScreen />;
    case 'CANCELED':
      return <CanceledScreen />;
    case 'PROCESSING':
      return <ProcessingScreen />;
    case 'ERROR':
      return <ErroredScreen />;
    default:
      return null;
  }
};

export const ScreenManager = () => {
  // Initialize Mirador ID tracking for fraud detection
  // This hook reads Mirador IDs (MUID, SID) from browser cookies set by Stripe.js
  useMiradorIds();

  const overlay = useSelector((state) => state.overlay);
  return (
    <view.div
      css={{
        margin: 'auto',
        stack: 'y',
        gap: 'medium',
        paddingLeft: 'medium',
        paddingRight: 'medium',
        maxWidth: '388px',
        alignItems: 'center',
        height: 'fill',
      }}
      data-testid="v2-screen-manager"
    >
      <ScreenComponent />
      {overlay === 'NETWORK_SWITCH' ? <NetworkSwitchingOverlay /> : null}
    </view.div>
  );
};
